<script setup lang="ts">
import type { ShipmentMetrics, ShipmentQueueKey } from '@/types/shipments';
import {
  SHIPMENT_DISPLAY_STATUS,
  SHIPMENT_QUEUE_FILTER,
} from '@/utils/shipment-status-ui';

const props = defineProps<{
  metrics: ShipmentMetrics;
  activeQueue: ShipmentQueueKey;
  activeMetricFilter?: string | null;
}>();

const emit = defineEmits<{
  (e: 'select', queue: ShipmentQueueKey): void;
  (e: 'metric-click', key: string): void;
}>();

const queues: { key: ShipmentQueueKey; label: string }[] = [
  { key: SHIPMENT_QUEUE_FILTER.all, label: 'All Shipments' },
  { key: SHIPMENT_DISPLAY_STATUS.Booked, label: SHIPMENT_DISPLAY_STATUS.Booked },
  { key: SHIPMENT_DISPLAY_STATUS.Active, label: SHIPMENT_DISPLAY_STATUS.Active },
  { key: SHIPMENT_QUEUE_FILTER.Late, label: SHIPMENT_QUEUE_FILTER.Late },
  {
    key: SHIPMENT_DISPLAY_STATUS.Delivered,
    label: SHIPMENT_DISPLAY_STATUS.Delivered,
  },
  { key: SHIPMENT_DISPLAY_STATUS.Closed, label: SHIPMENT_DISPLAY_STATUS.Closed },
  { key: SHIPMENT_QUEUE_FILTER.draft, label: 'Drafts' },
];

function getCount(key: ShipmentQueueKey): number {
  if (key === SHIPMENT_QUEUE_FILTER.all) return props.metrics.total;
  return props.metrics[key as keyof ShipmentMetrics] ?? 0;
}

function isQueueSelected(key: ShipmentQueueKey): boolean {
  return props.activeMetricFilter == null && props.activeQueue === key;
}
</script>

<template>
  <div class="flex h-full flex-col overflow-y-auto px-3 py-2">
    <h3
      class="mb-2 px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400"
    >
      Queues
    </h3>
    <ul class="space-y-0.5">
      <li v-for="q in queues" :key="q.key">
        <button
          type="button"
          class="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors"
          :class="
            isQueueSelected(q.key)
              ? 'bg-gray-100 font-medium text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          "
          @click="emit('select', q.key)"
        >
          <span class="flex items-center gap-2">
            <svg
              v-if="q.key === SHIPMENT_QUEUE_FILTER.all"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="size-4 text-gray-400"
            >
              <path
                d="M2 4.5A2.5 2.5 0 0 1 4.5 2h11a2.5 2.5 0 0 1 0 5h-11A2.5 2.5 0 0 1 2 4.5ZM2.75 9.083a.75.75 0 0 0 0 1.5h14.5a.75.75 0 0 0 0-1.5H2.75ZM2.75 12.663a.75.75 0 0 0 0 1.5h14.5a.75.75 0 0 0 0-1.5H2.75ZM2.75 16.25a.75.75 0 0 0 0 1.5h14.5a.75.75 0 1 0 0-1.5H2.75Z"
              />
            </svg>
            <svg
              v-else-if="q.key === SHIPMENT_DISPLAY_STATUS.Booked"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="size-4 text-purple-500"
            >
              <path
                fill-rule="evenodd"
                d="M15.988 3.012A2.25 2.25 0 0 1 18 5.25v6.5A2.25 2.25 0 0 1 15.75 14H13.5V7A2.5 2.5 0 0 0 11 4.5H8.128a2.252 2.252 0 0 1 1.884-1.488A2.25 2.25 0 0 1 12.25 1h1.5a2.25 2.25 0 0 1 2.238 2.012ZM11.5 3.25a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 .75.75v.25h-3v-.25Z"
                clip-rule="evenodd"
              />
              <path
                fill-rule="evenodd"
                d="M2 7a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7Zm2 3.25a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Zm0 3.5a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else-if="q.key === SHIPMENT_DISPLAY_STATUS.Active"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="size-4 text-blue-500"
            >
              <path
                fill-rule="evenodd"
                d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else-if="q.key === SHIPMENT_QUEUE_FILTER.Late"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="size-4 text-red-500"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else-if="q.key === SHIPMENT_DISPLAY_STATUS.Delivered"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="size-4 text-green-500"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else-if="q.key === SHIPMENT_QUEUE_FILTER.draft"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="size-4 text-gray-400"
            >
              <path
                d="M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z"
              />
              <path
                d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z"
              />
            </svg>
            <svg
              v-else-if="q.key === SHIPMENT_DISPLAY_STATUS.Closed"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="size-4 text-gray-400"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                clip-rule="evenodd"
              />
            </svg>
            {{ q.label }}
          </span>
          <span
            class="min-w-[24px] rounded-full px-1.5 py-0.5 text-center text-[11px] font-medium"
            :class="
              isQueueSelected(q.key)
                ? 'bg-gray-200 text-gray-700'
                : 'bg-gray-100 text-gray-500'
            "
          >
            {{ getCount(q.key) }}
          </span>
        </button>
      </li>
    </ul>

    <div class="my-3 border-t border-gray-200"></div>

    <ul class="space-y-0.5">
      <li>
        <button
          class="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors"
          :class="
            activeMetricFilter === 'picked_up_today'
              ? 'bg-gray-100 font-medium text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          "
          @click="emit('metric-click', 'picked_up_today')"
        >
          <span class="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="size-4 text-indigo-500"
            >
              <path
                d="M6.5 3c-1.051 0-2.093.04-3.125.117A1.49 1.49 0 0 0 2 4.607V10.5h9V4.606c0-.771-.59-1.43-1.375-1.489A41.568 41.568 0 0 0 6.5 3ZM2 12v2.5A1.5 1.5 0 0 0 3.5 16h.041a3 3 0 0 1 5.918 0h.791a.75.75 0 0 0 .75-.75V12H2Z"
              />
              <path
                d="M6.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM13.25 5a.75.75 0 0 0-.75.75v8.514a3.001 3.001 0 0 1 4.893 1.44c.37-.275.61-.719.595-1.227a24.905 24.905 0 0 0-1.784-8.549A1.486 1.486 0 0 0 14.823 5H13.25ZM14.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
              />
            </svg>
            Pickup Today
          </span>
          <span
            class="min-w-[24px] rounded-full px-1.5 py-0.5 text-center text-[11px] font-medium"
            :class="
              activeMetricFilter === 'picked_up_today'
                ? 'bg-gray-200 text-gray-700'
                : 'bg-gray-100 text-gray-500'
            "
          >
            {{ metrics.picked_up_today }}
          </span>
        </button>
      </li>
      <li>
        <button
          class="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors"
          :class="
            activeMetricFilter === 'delivery_today'
              ? 'bg-gray-100 font-medium text-gray-900'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          "
          @click="emit('metric-click', 'delivery_today')"
        >
          <span class="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="size-4 text-emerald-500"
            >
              <path
                fill-rule="evenodd"
                d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z"
                clip-rule="evenodd"
              />
            </svg>
            Delivery Today
          </span>
          <span
            class="min-w-[24px] rounded-full px-1.5 py-0.5 text-center text-[11px] font-medium"
            :class="
              activeMetricFilter === 'delivery_today'
                ? 'bg-gray-200 text-gray-700'
                : 'bg-gray-100 text-gray-500'
            "
          >
            {{ metrics.delivery_today }}
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>
